import { useContext, useEffect, useMemo, useRef, useState } from 'react'

import { Button, Checkbox, IconButton } from '@material-ui/core'
import { Delete } from '@material-ui/icons'
import AddIcon from '@material-ui/icons/Add'
import { EditContext } from 'Components/Hooks/ContextEdit'
import SetState from 'Components/Hooks/SetState'
import BasicTable from 'Components/UI/Atoms/BasicTable'
import useCustomSnackbar from 'Components/UI/Atoms/SnackBar'
import CancelConfirmButtons from 'Components/UI/Molecules/CancelConfirmButtons'
import InputsWithoutValidate from 'Components/UI/Molecules/InputsWithoutValidate'
import InputsWithValidate from 'Components/UI/Molecules/InputsWithValidate'
import DialogComponent from 'Components/UI/Molecules/Modals/DialogComponent'
import { Form, Formik } from 'formik'
import { interfaces } from 'service/interfaces'
import * as yup from 'yup'

const EMPRESA_ID_BY_LABEL = {
  ECO: 1,
  OEM: 2,
  DOS: 3,
}

const toNumber = (value) => {
  const number = Number(value)
  return Number.isFinite(number) ? number : 0
}

const getEmpresaId = (origen) => {
  if (typeof origen === 'number') return origen
  const normalized = String(origen || '')
    .trim()
    .toUpperCase()
  return EMPRESA_ID_BY_LABEL[normalized] || 1
}

const toMoney = (val) => {
  const num = Number(val) || 0
  return `$${num.toFixed(2)}`
}

const computeSaldoActual = (saldoAnterior, imputar) => {
  const sa = toNumber(saldoAnterior)
  const imp = toNumber(imputar)
  return sa - imp
}

const getComprobanteKey = (row = {}) =>
  `${row.tipo_comprobante}-${row.suc_comprobante}-${row.nro_comprobante}`

const extractArrayData = (response) => {
  if (Array.isArray(response?.data?.data)) return response.data.data
  if (Array.isArray(response?.data)) return response.data
  if (Array.isArray(response)) return response
  return []
}

const getErrorMessage = (error, fallback) => {
  return error?.response?.data?.data || error?.response?.data || fallback
}

const normalizeAssociatedRow = (row = {}) => {
  const tipo = row?.tipo_comprobante || ''
  const sucursal = row?.suc_comprobante ?? ''
  const numero = row?.nro_comprobante ?? ''
  const comprobante =
    row?.comprobante || (tipo ? `${tipo} ${sucursal}-${numero}`.trim() : `${sucursal}-${numero}`)

  return {
    id: row?.id,
    fecha: row?.fecha || '-',
    comprobante,
    saldo_anterior: toNumber(row?.saldo_anterior),
    saldo_actual: toNumber(row?.saldo_actual),
    imputar: toNumber(row?.imputar ?? row?.importe),
    tipo_comprobante: row?.tipo_comprobante,
    suc_comprobante: row?.suc_comprobante,
    nro_comprobante: row?.nro_comprobante,
  }
}

const normalizeInvoiceRow = (row = {}) => {
  return {
    id: getComprobanteKey(row),
    fecha: row?.fecha || '-',
    comprobante:
      row?.comprobante ||
      `${row?.tipo_comprobante || ''} ${row?.suc_comprobante || ''}-${row?.nro_comprobante || ''}`.trim(),
    saldo_anterior: toNumber(row?.saldo ?? row?.saldo_anterior),
    tipo_comprobante: row?.tipo_comprobante,
    suc_comprobante: row?.suc_comprobante,
    nro_comprobante: row?.nro_comprobante,
    asociado: Boolean(row?.asociado),
  }
}

const EditImputacionModal = (props) => {
  const { open, onClose, row, onConfirm } = props
  const formikRef = useRef(null)

  const validationSchema = yup.object().shape({
    imputar: yup
      .number()
      .typeError('Debe ser numérico')
      .min(0, 'Debe ser mayor o igual a 0')
      .max(Number(row?.saldo_anterior || 0), 'Debe ser menor o igual al saldo anterior')
      .required('Campo obligatorio'),
  })

  return (
    <DialogComponent
      open={open}
      handleclose={onClose}
      title="Editar imputación"
      disableBackdropClick={true}
      disableEscapeKeyDown={true}
      widthLarge="500px"
      disableChildHeight
      children={
        <Formik
          innerRef={formikRef}
          enableReinitialize
          initialValues={{
            comprobante: row?.comprobante || '',
            saldo_anterior: row?.saldo_anterior ?? 0,
            saldo_actual: computeSaldoActual(row?.saldo_anterior, row?.imputar),
            imputar: row?.imputar ?? 0,
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            onConfirm({
              imputar: Number(values.imputar) || 0,
            })
            onClose()
          }}
        >
          {({ values, setFieldValue, isSubmitting }) => (
            <Form>
              <InputsWithValidate
                fields={[
                  {
                    class: 'textfield',
                    name: 'comprobante',
                    label: 'Comprobante',
                    value: values.comprobante,
                    disabled: true,
                    sm: 12,
                    xs: 12,
                  },
                  {
                    class: 'textfield',
                    name: 'saldo_anterior',
                    label: 'Saldo anterior',
                    value: toMoney(values.saldo_anterior),
                    disabled: true,
                    sm: 6,
                    xs: 12,
                  },
                  {
                    class: 'textfield',
                    name: 'saldo_actual',
                    label: 'Saldo actual',
                    value: toMoney(computeSaldoActual(values.saldo_anterior, values.imputar)),
                    disabled: true,
                    sm: 6,
                    xs: 12,
                  },
                  {
                    class: 'textfield',
                    name: 'imputar',
                    label: 'Importe a imputar',
                    type: 'number',
                    value: values.imputar,
                    sm: 12,
                    xs: 12,
                  },
                ]}
                spacing={2}
                setFieldValue={setFieldValue}
                isSubmitting={isSubmitting}
              />
            </Form>
          )}
        </Formik>
      }
      actions={
        <CancelConfirmButtons
          onClickSubmit={() => {
            if (formikRef.current) formikRef.current.submitForm()
          }}
          onClickCancel={onClose}
          textButtonSubmit="CONFIRMAR"
        />
      }
    />
  )
}

const AsociarComprobanteModal = (props) => {
  const { open, onClose, existentes, onConfirm, origen, clienteId, ordenId } = props

  const [available, setAvailable] = useState([])
  const [selected, setSelected] = useState([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const existingKeys = useMemo(
    () => new Set((existentes || []).map((row) => getComprobanteKey(row))),
    [existentes]
  )

  useEffect(() => {
    if (!open) return

    let active = true

    const fetchInvoices = async () => {
      if (!origen || !clienteId || !ordenId) {
        setAvailable([])
        return
      }

      setLoading(true)
      try {
        const result = await interfaces.salesPaymentOrders.getInvoice(origen, clienteId, ordenId)
        if (!active) return
        setAvailable(extractArrayData(result).map(normalizeInvoiceRow))
      } catch (error) {
        if (!active) return
        setAvailable([])
      } finally {
        if (active) setLoading(false)
      }
    }

    setSelected([])
    fetchInvoices()

    return () => {
      active = false
    }
  }, [open, origen, clienteId, ordenId])

  const toggle = (row) => {
    const rowKey = getComprobanteKey(row)
    const isSelected = selected.some((s) => getComprobanteKey(s) === rowKey)
    if (isSelected) {
      setSelected(selected.filter((s) => getComprobanteKey(s) !== rowKey))
      return
    }
    setSelected([...selected, row])
  }

  const rows = available.map((row) => {
    const rowKey = getComprobanteKey(row)
    const isDisabled = row.asociado || existingKeys.has(rowKey)
    const isChecked = selected.some((s) => getComprobanteKey(s) === rowKey)

    return {
      seleccionar: (
        <Checkbox
          onChange={() => {
            if (isDisabled) return
            toggle(row)
          }}
          checked={isChecked}
          disabled={isDisabled || submitting || loading}
        />
      ),
      fecha: row.fecha,
      comprobante: row.comprobante,
      saldo: toMoney(row.saldo_anterior),
    }
  })

  return (
    <DialogComponent
      open={open}
      handleclose={onClose}
      title="Asociar comprobante"
      disableBackdropClick={true}
      disableEscapeKeyDown={true}
      widthLarge="800px"
      customHeight="600px"
      children={
        <div style={{ margin: '1rem', overflow: 'auto' }}>
          <BasicTable
            col={[
              { title: '', style: { width: '40px' } },
              { title: 'Fecha', style: { width: '120px' } },
              { title: 'Comprobante', style: { width: '240px' } },
              { title: 'Saldo', style: { width: '120px' } },
            ]}
            size="small"
            rows={rows}
          />
        </div>
      }
      actions={
        <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
          <Button onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            onClick={async () => {
              setSubmitting(true)
              const success = await onConfirm(selected)
              setSubmitting(false)
              if (success) {
                setSelected([])
                onClose()
              }
            }}
            color="primary"
            variant="contained"
            disabled={selected.length === 0 || submitting || loading}
            style={{ marginLeft: '10px' }}
          >
            Confirmar
          </Button>
        </div>
      }
    />
  )
}

const ComprobantesContent = (props) => {
  const { data, setData, formikRef } = props
  const [edit, setEdit, , setIsEdited] = useContext(EditContext)
  const createDismissableSnackbar = useCustomSnackbar()

  const { state: modalAsociar, setOpen: openAsociar, setClose: closeAsociar } = SetState()
  const { state: modalEditar, setOpen: openEditar, setClose: closeEditar } = SetState()

  const [selectedRow, setSelectedRow] = useState(null)

  const empresaId = useMemo(
    () => getEmpresaId(data?.paymentOrder?.origen),
    [data?.paymentOrder?.origen]
  )
  const orderId = useMemo(() => toNumber(data?.paymentOrder?.id), [data?.paymentOrder?.id])
  const clientId = useMemo(() => toNumber(data?.cliente?.id), [data?.cliente?.id])

  const [rows, setRows] = useState(() =>
    (data?.comprobantes || data?.comprobantesAsociados || []).map(normalizeAssociatedRow)
  )

  useEffect(() => {
    setRows((data?.comprobantes || data?.comprobantesAsociados || []).map(normalizeAssociatedRow))
  }, [data?.comprobantes, data?.comprobantesAsociados])

  const totalMediosPago = useMemo(
    () => toNumber(data?.paymentOrder?.total ?? data?.info?.importe_total),
    [data?.paymentOrder?.total, data?.info?.importe_total]
  )

  const totalAImputar = useMemo(
    () => (rows || []).reduce((acc, row) => acc + toNumber(row.imputar), 0),
    [rows]
  )

  const saldoOP = useMemo(() => totalMediosPago - totalAImputar, [totalMediosPago, totalAImputar])

  const refreshOrder = async () => {
    if (!orderId) return false
    try {
      const updated = await interfaces.salesPaymentOrders.getOneForEdit(orderId, empresaId)
      setData(updated?.data || data)
      return true
    } catch (error) {
      createDismissableSnackbar(getErrorMessage(error, 'No se pudo refrescar la orden'), {
        variant: 'error',
      })
      return false
    }
  }

  const handleDelete = async (id) => {
    if (!edit || !id) return

    try {
      await interfaces.salesPaymentOrders.unassociateComp({
        origen: empresaId,
        id,
      })
      await refreshOrder()
      setEdit(false)
      setIsEdited(false)
      createDismissableSnackbar('Comprobante desasociado correctamente', { variant: 'success' })
    } catch (error) {
      createDismissableSnackbar(getErrorMessage(error, 'Error al desasociar comprobante'), {
        variant: 'error',
      })
    }
  }

  const iconDelete = (id) => (
    <IconButton
      onClick={(event) => {
        event.stopPropagation()
        handleDelete(id)
      }}
      disabled={!edit}
      style={{ padding: '0px' }}
    >
      <Delete />
    </IconButton>
  )

  const tableRows = useMemo(
    () =>
      (rows || []).map((row) => ({
        fecha: row.fecha,
        comprobante: row.comprobante,
        saldo_anterior: toMoney(row.saldo_anterior),
        saldo_actual: toMoney(computeSaldoActual(row.saldo_anterior, row.imputar)),
        imputar: toMoney(row.imputar),
        action: iconDelete(row.id),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, edit]
  )

  const onClickRow = (_, index) => {
    if (!edit) return
    const row = rows[index]
    if (!row) return
    setSelectedRow(row)
    openEditar(true)
  }

  const onConfirmEdit = (payload) => {
    const next = (rows || []).map((row) =>
      row.id === selectedRow.id ? { ...row, ...payload } : row
    )
    setRows(next)
    setData((prev) => ({
      ...prev,
      comprobantes: next,
      comprobantesAsociados: next,
      info: {
        ...(prev?.info || {}),
        total_a_imputar: next.reduce((acc, row) => acc + toNumber(row.imputar), 0),
        saldo_op: totalMediosPago - next.reduce((acc, row) => acc + toNumber(row.imputar), 0),
      },
    }))
  }

  const onConfirmAsociar = async (selected) => {
    if (!edit || !orderId || !empresaId) return false
    if (!Array.isArray(selected) || selected.length === 0) return false

    try {
      await interfaces.salesPaymentOrders.associateComp({
        origen: empresaId,
        orden: orderId,
        comprobantes: selected.map((row) => ({
          tipo_comprobante: row.tipo_comprobante,
          suc_comprobante: row.suc_comprobante,
          nro_comprobante: row.nro_comprobante,
        })),
      })
      await refreshOrder()
      setEdit(false)
      setIsEdited(false)
      createDismissableSnackbar('Comprobantes asociados con éxito', { variant: 'success' })
      return true
    } catch (error) {
      createDismissableSnackbar(getErrorMessage(error, 'Error al asociar comprobantes'), {
        variant: 'error',
      })
      return false
    }
  }

  const schema = yup.object().shape({ _dummy: yup.mixed() })

  return (
    <Formik
      innerRef={formikRef}
      enableReinitialize
      initialValues={{ _dummy: '' }}
      validationSchema={schema}
      onSubmit={() => {
        setData((prev) => ({
          ...prev,
          comprobantes: rows,
          comprobantesAsociados: rows,
          info: {
            ...(prev?.info || {}),
            total_a_imputar: totalAImputar,
            saldo_op: saldoOP,
          },
        }))
      }}
    >
      {() => (
        <Form>
          <div style={{ margin: '2rem' }}>
            <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'flex-start' }}>
              <InputsWithoutValidate
                fields={[
                  {
                    class: 'textfield',
                    name: 'saldo_op',
                    label: 'Saldo O.P.',
                    value: toMoney(saldoOP),
                    disabled: true,
                    sm: 6,
                    xs: 12,
                  },
                  {
                    class: 'textfield',
                    name: 'total_a_imputar',
                    label: 'Total a imputar',
                    value: toMoney(totalAImputar),
                    disabled: true,
                    sm: 6,
                    xs: 12,
                  },
                ]}
                spacing={2}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <BasicTable
                  col={[
                    { title: 'Fecha', style: { width: '120px' } },
                    { title: 'Comprobante', style: { width: '220px' } },
                    { title: 'Saldo anterior', style: { width: '140px' } },
                    { title: 'Saldo actual', style: { width: '140px' } },
                    { title: 'Importe a imputar', style: { width: '140px' } },
                    { title: '', style: { width: '40px' } },
                  ]}
                  size="small"
                  rows={tableRows}
                  onClickRow={onClickRow}
                />
              </div>

              <div style={{ marginLeft: '1rem', marginTop: '8px' }}>
                <Button
                  onClick={() => openAsociar(true)}
                  disabled={!edit || !orderId || !clientId}
                  color="primary"
                  variant="contained"
                  size="large"
                  style={{
                    height: '40px',
                    marginRight: '1rem',
                    marginLeft: '.5rem',
                  }}
                >
                  <AddIcon />
                </Button>
              </div>
            </div>
          </div>

          {modalAsociar.suspense && (
            <AsociarComprobanteModal
              open={modalAsociar.open}
              onClose={closeAsociar}
              existentes={rows}
              onConfirm={onConfirmAsociar}
              origen={empresaId}
              clienteId={clientId}
              ordenId={orderId}
            />
          )}

          {modalEditar.suspense && selectedRow && (
            <EditImputacionModal
              open={modalEditar.open}
              onClose={closeEditar}
              row={selectedRow}
              onConfirm={onConfirmEdit}
            />
          )}
        </Form>
      )}
    </Formik>
  )
}

export default ComprobantesContent
